import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "@/contexts/I18nContext";
import AppShell from "@/components/layout/AppShell";
import { toast } from "sonner";
import TopicSuggestions from "@/components/topic/TopicSuggestions";
import {
  Sparkles, Upload, Search, X, Trash2, Pencil, Download, Eye,
  Video as VideoIcon, Loader2, Check, Play, Film, Clock, ArrowRight,
} from "lucide-react";

// Video Studio — Phase 1 (UI + backend scaffolding).
// No AI video-generation call is made yet; the backend simulates the
// queued → generating → failed lifecycle so every UI state is exercised.
// Flip VIDEO_PROVIDER_ENABLED in backend .env and drop the real provider
// call into videos.py::_call_video_provider to go live.

const TOKEN_KEY = "nx_token";
const getToken = () => localStorage.getItem(TOKEN_KEY) || "";

const FILTERS = [
  { key: "all",      label: { ar: "الكل",                 en: "All" } },
  { key: "uploaded", label: { ar: "مرفوعة",               en: "Uploaded" } },
  { key: "ai",       label: { ar: "مولدة بالذكاء الاصطناعي", en: "AI-generated" } },
  { key: "ready",    label: { ar: "جاهزة",                en: "Ready" } },
];

const ASPECT_OPTIONS = [
  // Feb-2026: only 16:9 is honestly supported by the wired provider
  // (Sora-2 delivers 1280x720 landscape). Other aspects previously
  // advertised were fabricated by stretched-crop; hidden until a
  // provider that natively delivers vertical/square is wired.
  { key: "16:9", label: { ar: "أفقي · 16:9",  en: "Landscape · 16:9" } },
];

// 8s is delivered by stitching two native 4s Sora-2 scenes.
const DURATION_OPTIONS = [8];

const STYLE_OPTIONS = [
  { key: "auto",      label: { ar: "أفضل ستايل تلقائياً", en: "Best style automatically" } },
  { key: "realistic", label: { ar: "واقعي",               en: "Realistic" } },
  { key: "modern",    label: { ar: "عصري",                en: "Modern" } },
  { key: "cinematic", label: { ar: "سينمائي",             en: "Cinematic" } },
  { key: "minimal",   label: { ar: "بسيط",                en: "Minimal" } },
  { key: "luxury",    label: { ar: "فاخر",                en: "Luxury" } },
];

const ACCEPT = "video/mp4,video/quicktime,video/webm";
const MAX_MB = 100;

const fmtBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};
const fmtDate = (iso, lang) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
};
const fmtDuration = (sec) => sec ? `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}` : "—";

// Cinematic pipeline stage → localized copy. Backend emits English canonical
// stages; the UI translates them into fluent Arabic (or falls back to the
// backend's stage_message if a stage isn't in the map yet).
const STAGE_COPY = {
  planning:   { ar: "كلود يرسم قصة الفيديو…",       en: "Claude is sketching the storyboard…" },
  shooting:   { ar: "تصوير المشاهد…",                 en: "Filming the scenes…" },
  stitching:  { ar: "دمج المشاهد بمؤثرات سينمائية…", en: "Editing scenes with cinematic transitions…" },
  finalizing: { ar: "إضافة شعارك والبطاقة الختامية…", en: "Adding your logo and closing card…" },
  ready:      { ar: "الفيديو جاهز",                    en: "Your DAAM cinematic is ready" },
  failed:     { ar: "فشل الإنشاء",                     en: "Generation failed" },
};

const stageCopy = (stage, fallback, lang) => {
  const m = STAGE_COPY[stage];
  if (m) return m[lang] || m.en;
  return fallback || "";
};

// Auth-carrying <video> source. Fetches as blob so the <video src> can
// carry the Bearer token; retries once on transient errors so freshly-
// generated videos become viewable as soon as storage settles.
function useAuthedVideo(videoId, enabled = true) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    const token = getToken();
    if (!enabled || !videoId || !token) return;
    let revoked = null;
    let cancelled = false;
    const doFetch = async (attempt = 0) => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/videos/${videoId}/file`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) {
          if (attempt < 1 && [404, 500, 502, 503].includes(res.status)) {
            setTimeout(() => { if (!cancelled) doFetch(attempt + 1); }, 1500);
            return;
          }
          throw new Error(`HTTP ${res.status}`);
        }
        const b = await res.blob();
        if (cancelled) return;
        const u = URL.createObjectURL(b);
        revoked = u;
        setUrl(u);
      } catch {
        if (!cancelled) setUrl(null);
      }
    };
    doFetch(0);
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [videoId, enabled]);
  return url;
}

// ------- Small helpers -------
function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
  });
}

// ------- Status badge -------
function StatusBadge({ status, lang }) {
  const map = {
    queued:     { ar: "في الانتظار", en: "Queued",     cls: "bg-muted text-muted-foreground" },
    generating: { ar: "جارٍ الإنشاء", en: "Generating", cls: "bg-primary/90 text-primary-foreground" },
    ready:      { ar: "جاهز",        en: "Ready",      cls: "bg-emerald-500/90 text-white" },
    failed:     { ar: "فشل",         en: "Failed",     cls: "bg-red-500/90 text-white" },
  };
  const it = map[status] || map.queued;
  return (
    <span
      data-testid={`video-status-${status}`}
      className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-sm ${it.cls}`}
    >
      {status === "generating" && <Loader2 className="w-3 h-3 animate-spin" />}
      {it[lang] || it.en}
    </span>
  );
}

// ------- Card -------
function VideoCard({ v, lang, onOpen, onRename, onDelete, onDownload }) {
  const ready = v.status === "ready";
  const url = useAuthedVideo(v.id, ready);
  return (
    <div
      data-testid={`video-card-${v.id}`}
      className="group relative bg-card border border-border/60 rounded-md overflow-hidden hover:border-primary/60 transition-colors"
    >
      <div className="aspect-[9/16] bg-muted/30 relative flex items-center justify-center">
        {ready && url ? (
          <video
            data-testid={`video-thumb-${v.id}`}
            src={url}
            muted loop playsInline
            className="w-full h-full object-cover"
            onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
            onMouseLeave={(e) => { try { e.currentTarget.pause(); e.currentTarget.currentTime = 0; } catch {} }}
          />
        ) : v.status === "generating" ? (
          <div className="flex flex-col items-center gap-2 text-muted-foreground text-center px-3" data-testid={`video-generating-${v.id}`}>
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <span className="text-[10px] font-medium text-foreground/80 line-clamp-2 leading-tight" dir="auto">
              {stageCopy(v.stage, v.stage_message, lang) || (lang === "ar" ? "جارٍ الإنشاء…" : "Generating…")}
            </span>
            {v.scene_total > 0 && (
              <span className="text-[9px] text-muted-foreground/80">
                {lang === "ar"
                  ? `مشهد ${v.scene_current || 0} من ${v.scene_total}`
                  : `Scene ${v.scene_current || 0} of ${v.scene_total}`}
              </span>
            )}
            {typeof v.stage_pct === "number" && v.stage_pct > 0 && (
              <div className="w-24 h-0.5 bg-muted/60 rounded-full overflow-hidden mt-1">
                <div
                  className="h-full bg-primary transition-all duration-500"
                  style={{ width: `${v.stage_pct}%` }}
                  data-testid={`video-progress-${v.id}`}
                />
              </div>
            )}
          </div>
        ) : v.status === "failed" ? (
          <div className="flex flex-col items-center gap-1 text-muted-foreground text-center px-3">
            <X className="w-6 h-6 text-red-500" />
            <span className="text-[10px] leading-snug line-clamp-3">{v.error_message || (lang === "ar" ? "فشل الإنشاء" : "Generation failed")}</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Clock className="w-6 h-6" />
            <span className="text-[10px]">{lang === "ar" ? "في الانتظار…" : "Queued…"}</span>
          </div>
        )}

        {/* Source badge */}
        <span
          className={`absolute top-2 start-2 text-[10px] px-2 py-0.5 rounded-sm backdrop-blur-sm ${
            v.source === "ai" ? "bg-primary/80 text-primary-foreground" : "bg-background/80 text-foreground"
          }`}
        >
          {v.source === "ai" ? (lang === "ar" ? "AI" : "AI") : (lang === "ar" ? "مرفوع" : "Uploaded")}
        </span>

        {/* Status badge */}
        <div className="absolute top-2 end-2">
          <StatusBadge status={v.status} lang={lang} />
        </div>

        {/* Duration overlay when ready */}
        {ready && v.duration_seconds > 0 && (
          <span className="absolute bottom-2 end-2 text-[10px] px-2 py-0.5 rounded-sm bg-black/70 text-white">
            {fmtDuration(v.duration_seconds)}
          </span>
        )}
      </div>

      <div className="p-2.5 flex items-center gap-1">
        <button
          data-testid={`video-action-view-${v.id}`}
          onClick={() => onOpen(v)}
          className="flex-1 text-xs font-medium truncate text-start hover:text-primary transition-colors"
          title={v.name}
        >
          {v.name}
        </button>
        <button
          data-testid={`video-action-rename-${v.id}`}
          onClick={() => onRename(v)}
          className="p-1 hover:bg-muted rounded-sm text-muted-foreground hover:text-foreground"
          title={lang === "ar" ? "إعادة تسمية" : "Rename"}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        {ready && (
          <button
            data-testid={`video-action-download-${v.id}`}
            onClick={() => onDownload(v)}
            className="p-1 hover:bg-muted rounded-sm text-muted-foreground hover:text-foreground"
            title={lang === "ar" ? "تحميل" : "Download"}
          >
            <Download className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          data-testid={`video-action-delete-${v.id}`}
          onClick={() => onDelete(v)}
          className="p-1 hover:bg-muted rounded-sm text-muted-foreground hover:text-red-500"
          title={lang === "ar" ? "حذف" : "Delete"}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ------- Generate modal -------
function GenerateModal({ open, lang, onClose, onSubmit }) {
  const [prompt, setPrompt]     = useState("");
  const [aspect, setAspect]     = useState("16:9");
  const [duration, setDuration] = useState(8);
  const [style, setStyle]       = useState("auto");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setPrompt(""); setAspect("16:9"); setDuration(8); setStyle("auto");
      setShowAdvanced(false); setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;
  const canSubmit = prompt.trim().length > 0 && !submitting;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-card border border-border/60 rounded-md w-full max-w-2xl max-h-[90vh] overflow-auto p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="video-generate-modal"
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold inline-flex items-center gap-2">
              <Film className="w-5 h-5 text-primary" />
              {lang === "ar" ? "إنشاء فيديو سينمائي" : "Create a cinematic video"}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {lang === "ar"
                ? "اكتب موضوعاً فقط — دَعم يكتب القصة، يصور المشاهد، ويدمج كل شيء تلقائياً"
                : "Just type a topic — DAAM writes the story, shoots the scenes, and edits it all together"}
            </p>
            {/* Honesty badge (Feb-2026): the wired provider only delivers a
                single 4s landscape clip today. Do NOT claim vertical/square
                support until a provider that genuinely returns those aspects
                is wired. */}
            <div
              data-testid="video-preview-badge"
              className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-medium px-2 py-1 rounded-sm bg-amber-500/10 text-amber-600 border border-amber-500/30"
            >
              <VideoIcon className="w-3 h-3" />
              {lang === "ar"
                ? "فيديو تجريبي — أفقي 8 ثوانٍ"
                : "Preview video — landscape · 8 seconds"}
            </div>
          </div>
          <button data-testid="video-generate-close" className="p-1 hover:bg-muted rounded-sm" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <label className="block text-xs font-medium text-muted-foreground mb-1">{lang === "ar" ? "الموضوع" : "Topic"}</label>
        <textarea
          data-testid="video-generate-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={lang === "ar"
            ? "مثال: شروق الشمس على بحيرات إنترلاكن"
            : "e.g. Sunrise over the lakes of Interlaken"}
          rows={3}
          className="w-full text-sm bg-muted/30 border border-border/60 rounded-md p-3 focus:outline-none focus:border-primary/60"
          dir="auto"
        />
        <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
          {lang === "ar"
            ? "دَعم يخطط قصة كاملة، يصور المشاهد بكاميرا واحدة وإضاءة موحدة، ويضيف شعارك والبطاقة الختامية تلقائياً."
            : "DAAM plans the full storyboard, shoots every scene with a matching camera and lighting, and adds your logo + closing card automatically."}
        </p>

        <div className="mt-4">
          <TopicSuggestions
            lang={lang}
            onPick={(t) => setPrompt(t)}
            testIdPrefix="video-generate-suggestions"
          />
        </div>

        <div className="mt-4">
          <button
            type="button"
            data-testid="video-generate-advanced-toggle"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <ArrowRight className={`w-3 h-3 transition-transform ${showAdvanced ? "rotate-90" : ""}`} />
            {lang === "ar" ? "خيارات متقدمة" : "Advanced options"}
          </button>
          {showAdvanced && (
            <div className="mt-2 p-3 rounded-sm bg-muted/30 border border-border/40 space-y-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-2">{lang === "ar" ? "النسبة" : "Aspect ratio"}</label>
                <div className="flex flex-wrap gap-2">
                  {ASPECT_OPTIONS.map((o) => (
                    <button
                      key={o.key}
                      data-testid={`video-aspect-${o.key.replace(":", "-")}`}
                      onClick={() => setAspect(o.key)}
                      className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
                        aspect === o.key
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border/60 hover:border-primary/60"
                      }`}
                    >
                      {aspect === o.key && <Check className="w-3 h-3 inline me-1" />}
                      {o.label[lang] || o.label.en}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-2">{lang === "ar" ? "المدة" : "Duration"}</label>
                <div className="flex gap-2">
                  {DURATION_OPTIONS.map((d) => (
                    <button
                      key={d}
                      data-testid={`video-duration-${d}`}
                      onClick={() => setDuration(d)}
                      className={`text-xs px-4 py-1.5 rounded-sm border transition-colors ${
                        duration === d
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border/60 hover:border-primary/60"
                      }`}
                    >
                      {d}s
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-2">{lang === "ar" ? "الستايل" : "Style"}</label>
                <div className="flex flex-wrap gap-2">
                  {STYLE_OPTIONS.map((o) => (
                    <button
                      key={o.key}
                      data-testid={`video-style-${o.key}`}
                      onClick={() => setStyle(o.key)}
                      className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
                        style === o.key
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border/60 hover:border-primary/60"
                      }`}
                    >
                      {style === o.key && <Check className="w-3 h-3 inline me-1" />}
                      {o.label[lang] || o.label.en}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Duration hint always visible so user knows what they're getting */}
        <div className="mt-3 text-[10px] text-muted-foreground">
          {lang === "ar"
            ? `سيتم إنشاء فيديو ${duration} ثانية بنسبة ${aspect}. عدّل من الخيارات المتقدمة.`
            : `Will produce a ${duration}s ${aspect} cinematic. Adjust from Advanced options.`}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            data-testid="video-generate-cancel"
            onClick={onClose}
            className="text-xs px-4 py-2 rounded-sm border border-border/60 hover:bg-muted"
          >
            {lang === "ar" ? "إلغاء" : "Cancel"}
          </button>
          <button
            data-testid="video-generate-submit"
            disabled={!canSubmit}
            onClick={async () => {
              setSubmitting(true);
              try {
                await onSubmit({
                  prompt: prompt.trim(),
                  aspect_ratio: aspect,
                  duration_seconds: duration,
                  style: style === "auto" ? null : style,
                });
              } finally { setSubmitting(false); }
            }}
            className="inline-flex items-center gap-2 text-xs px-5 py-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {submitting
              ? (lang === "ar" ? "جارٍ البدء…" : "Starting…")
              : (lang === "ar" ? "إنشاء الفيديو" : "Create video")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------- Rename modal -------
function RenameModal({ target, lang, onClose, onSaved }) {
  const [name, setName] = useState(target?.name || "");
  useEffect(() => { setName(target?.name || ""); }, [target]);
  if (!target) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border/60 rounded-md w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="video-rename-modal">
        <h3 className="text-base font-semibold mb-3">{lang === "ar" ? "إعادة تسمية الفيديو" : "Rename video"}</h3>
        <input
          data-testid="video-rename-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full text-sm bg-muted/30 border border-border/60 rounded-md p-2.5 focus:outline-none focus:border-primary/60"
          dir="auto"
        />
        <div className="mt-4 flex items-center justify-end gap-2">
          <button className="text-xs px-3 py-1.5 rounded-sm border border-border/60 hover:bg-muted" onClick={onClose}>{lang === "ar" ? "إلغاء" : "Cancel"}</button>
          <button
            data-testid="video-rename-save"
            disabled={!name.trim() || name.trim() === target.name}
            onClick={async () => {
              try {
                const r = await apiFetch(`/api/videos/${target.id}`, {
                  method: "PATCH",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ name: name.trim() }),
                });
                if (!r.ok) throw new Error();
                const updated = await r.json();
                onSaved(updated);
                toast.success(lang === "ar" ? "تم التحديث" : "Renamed");
              } catch {
                toast.error(lang === "ar" ? "فشل التحديث" : "Rename failed");
              }
            }}
            className="text-xs px-4 py-1.5 rounded-sm bg-primary text-primary-foreground disabled:opacity-50"
          >
            {lang === "ar" ? "حفظ" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------- Details modal -------
function DetailsModal({ target, lang, onClose, onDownload }) {
  const url = useAuthedVideo(target?.status === "ready" ? target.id : null);
  if (!target) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border/60 rounded-md w-full max-w-4xl max-h-[90vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="video-details-modal">
        <div className="flex justify-between items-start mb-4 gap-4">
          <h3 className="text-lg font-semibold truncate">{target.name}</h3>
          <button data-testid="video-details-close" className="p-1 hover:bg-muted rounded-sm shrink-0" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="grid md:grid-cols-[2fr_1fr] gap-5">
          <div className="bg-black rounded-md min-h-[300px] flex items-center justify-center overflow-hidden">
            {target.status === "ready" && url ? (
              <video src={url} controls className="max-w-full max-h-[70vh]" data-testid="video-details-player" />
            ) : target.status === "failed" ? (
              <div className="flex flex-col items-center gap-2 text-white text-center px-6 py-10">
                <X className="w-10 h-10 text-red-400" />
                <div className="text-sm">{target.error_message || (lang === "ar" ? "فشل الإنشاء" : "Generation failed")}</div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-white/70 py-10">
                <Loader2 className="w-8 h-8 animate-spin" />
                <div className="text-sm">{lang === "ar" ? "لم يجهز الفيديو بعد" : "Video not ready yet"}</div>
              </div>
            )}
          </div>
          <dl className="text-sm space-y-3">
            <div>
              <dt className="text-muted-foreground text-xs">{lang === "ar" ? "الحالة" : "Status"}</dt>
              <dd className="font-medium"><StatusBadge status={target.status} lang={lang} /></dd>
            </div>
            {target.prompt && (
              <div>
                <dt className="text-muted-foreground text-xs">{lang === "ar" ? "الوصف" : "Prompt"}</dt>
                <dd className="text-xs leading-relaxed" dir="auto">{target.prompt}</dd>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <dt className="text-muted-foreground text-xs">{lang === "ar" ? "النسبة" : "Aspect"}</dt>
                <dd className="font-medium">{target.aspect_ratio}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">{lang === "ar" ? "المدة" : "Duration"}</dt>
                <dd className="font-medium">{target.duration_seconds ? `${target.duration_seconds}s` : "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">{lang === "ar" ? "الحجم" : "Size"}</dt>
                <dd className="font-medium">{fmtBytes(target.file_size)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">{lang === "ar" ? "الستايل" : "Style"}</dt>
                <dd className="font-medium">{target.style || "—"}</dd>
              </div>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">{lang === "ar" ? "تاريخ الإنشاء" : "Created"}</dt>
              <dd className="font-medium">{fmtDate(target.created_at, lang)}</dd>
            </div>
            {target.error_message && (
              <div>
                <dt className="text-muted-foreground text-xs">{lang === "ar" ? "الخطأ" : "Error"}</dt>
                <dd className="text-xs text-red-500 leading-relaxed">{target.error_message}</dd>
              </div>
            )}
            {target.status === "ready" && (
              <button
                data-testid="video-details-download"
                onClick={() => onDownload(target)}
                className="w-full inline-flex items-center justify-center gap-2 text-xs px-4 py-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Download className="w-3.5 h-3.5" />
                {lang === "ar" ? "تحميل" : "Download"}
              </button>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
}

// ------- Main page -------
export default function VideosPage() {
  const { lang } = useI18n();
  const [videos, setVideos] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter && filter !== "all") params.set("source", filter);
      if (q.trim()) params.set("q", q.trim());
      const r = await apiFetch(`/api/videos?${params.toString()}`);
      if (!r.ok) throw new Error();
      const d = await r.json();
      setVideos(d.videos || []);
    } catch {
      toast.error(lang === "ar" ? "فشل تحميل الفيديوهات" : "Failed to load videos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => {
    const t = setTimeout(() => load(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  // Poll every 3s while any video is queued or generating so the cinematic
  // pipeline's per-scene progress feels live.
  useEffect(() => {
    if (!videos.some((v) => v.status === "queued" || v.status === "generating")) return;
    const t = setInterval(() => load(), 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [videos]);

  const handleGenerate = async (payload) => {
    try {
      // Idempotency key generated per-submit — the backend uses this to
      // dedupe accidental double-clicks / network retries so users are
      // never billed for a duplicate Sora job.
      const idempotencyKey = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const r = await apiFetch("/api/videos/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, idempotency_key: idempotencyKey }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        // Circuit-breaker fail-fast returns 503 with a friendly message.
        const msg = err?.detail?.message_ar || err?.detail?.message_en || err.detail || "generate failed";
        throw new Error(msg);
      }
      const v = await r.json();
      setVideos((prev) => {
        // Same-id => idempotent replay: update in place instead of duplicating.
        const has = prev.find((x) => x.id === v.id);
        return has ? prev.map((x) => (x.id === v.id ? v : x)) : [v, ...prev];
      });
      setGenerateOpen(false);
      toast.success(lang === "ar" ? "دَعم يبدأ العمل على فيديوك…" : "DAAM is starting on your video…");
    } catch (e) {
      toast.error(lang === "ar" ? `فشل الطلب: ${e.message}` : `Request failed: ${e.message}`);
    }
  };

  const handleUpload = async (file) => {
    if (!ACCEPT.split(",").includes(file.type)) {
      toast.error(lang === "ar" ? "نوع الملف غير مدعوم" : "Unsupported file type");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(lang === "ar" ? `الملف كبير جداً (${MAX_MB}MB كحد أقصى)` : `File too large (max ${MAX_MB}MB)`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await apiFetch("/api/videos/upload", { method: "POST", body: fd });
      if (!r.ok) throw new Error();
      const v = await r.json();
      setVideos((prev) => [v, ...prev]);
      toast.success(lang === "ar" ? "تم الرفع" : "Uploaded");
    } catch {
      toast.error(lang === "ar" ? "فشل الرفع" : "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (v) => {
    if (!window.confirm(lang === "ar" ? `حذف "${v.name}"؟` : `Delete "${v.name}"?`)) return;
    try {
      const r = await apiFetch(`/api/videos/${v.id}`, { method: "DELETE" });
      if (!r.ok) throw new Error();
      setVideos((prev) => prev.filter((x) => x.id !== v.id));
      toast.success(lang === "ar" ? "تم الحذف" : "Deleted");
    } catch {
      toast.error(lang === "ar" ? "فشل الحذف" : "Delete failed");
    }
  };

  const handleDownload = async (v) => {
    try {
      const token = getToken();
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/videos/${v.id}/file?download=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const fileName = `${(v.name || "video").replace(/[^\w\d\-. ]+/g, "-").slice(0, 60)}.mp4`;
      const file = new File([blob], fileName, { type: "video/mp4" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: v.name });
          return;
        } catch (e) {
          if (e && e.name === "AbortError") return;
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fileName; a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      toast.error(lang === "ar" ? "تعذّر التحميل" : "Download failed");
    }
  };

  const handleRenamed = (updated) => {
    setVideos((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
    setRenameTarget(null);
  };

  return (
    <AppShell>
      <div data-testid="videos-page" className="space-y-4">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-display font-semibold flex items-center gap-2">
              <Film className="w-6 h-6 text-primary" />
              {lang === "ar" ? "استوديو الفيديو" : "Video Studio"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {lang === "ar"
                ? "اكتب موضوعاً — دَعم يخرج القصة، يصور المشاهد، ويسلّمك فيديو جاهزاً للنشر"
                : "Type a topic — DAAM directs the story, shoots the scenes, and hands you a ready-to-publish cinematic"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
              className="hidden"
              data-testid="videos-upload-input"
            />
            <button
              data-testid="videos-upload-btn"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 text-xs px-4 py-2 rounded-sm border border-border/60 hover:border-primary/60 disabled:opacity-50"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {lang === "ar" ? "رفع" : "Upload"}
            </button>
            <button
              data-testid="videos-ai-generate-btn"
              onClick={() => setGenerateOpen(true)}
              className="inline-flex items-center gap-2 text-xs px-4 py-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {lang === "ar" ? "إنشاء بالذكاء الاصطناعي" : "Generate with AI"}
            </button>
          </div>
        </header>

        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`videos-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
                filter === f.key
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border/60 hover:border-primary/60"
              }`}
            >
              {f.label[lang] || f.label.en}
            </button>
          ))}
          <div className="ms-auto relative">
            <Search className="w-3.5 h-3.5 absolute top-1/2 -translate-y-1/2 start-2.5 text-muted-foreground" />
            <input
              data-testid="videos-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={lang === "ar" ? "بحث…" : "Search…"}
              className="text-xs ps-8 pe-3 py-1.5 bg-muted/30 border border-border/60 rounded-sm focus:outline-none focus:border-primary/60 w-48"
            />
          </div>
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : videos.length === 0 ? (
          <div
            data-testid="videos-empty"
            className="border border-dashed border-border/60 rounded-md py-20 text-center"
          >
            <VideoIcon className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              {lang === "ar" ? "أنشئ أول فيديو سينمائي لك" : "Create your first cinematic"}
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1 max-w-md mx-auto leading-relaxed">
              {lang === "ar"
                ? "اكتب موضوعاً واحداً — دَعم يخطط القصة، يصور المشاهد، ويسلّمك فيديو جاهزاً للنشر"
                : "Type one topic — DAAM plans the story, shoots the scenes, and delivers a ready-to-publish video"}
            </p>
            <button
              data-testid="videos-empty-cta"
              onClick={() => setGenerateOpen(true)}
              className="mt-5 inline-flex items-center gap-2 text-xs px-4 py-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {lang === "ar" ? "ابدأ الآن" : "Start now"}
            </button>
          </div>
        ) : (
          <div data-testid="videos-grid" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
            {videos.map((v) => (
              <VideoCard
                key={v.id}
                v={v}
                lang={lang}
                onOpen={setDetailsTarget}
                onRename={setRenameTarget}
                onDelete={handleDelete}
                onDownload={handleDownload}
              />
            ))}
          </div>
        )}
      </div>

      <GenerateModal
        open={generateOpen}
        lang={lang}
        onClose={() => setGenerateOpen(false)}
        onSubmit={handleGenerate}
      />
      <RenameModal
        target={renameTarget}
        lang={lang}
        onClose={() => setRenameTarget(null)}
        onSaved={handleRenamed}
      />
      <DetailsModal
        target={detailsTarget}
        lang={lang}
        onClose={() => setDetailsTarget(null)}
        onDownload={handleDownload}
      />
    </AppShell>
  );
}
